/* eslint-disable camelcase */
import {
  cn,
  Divider,
  RiCheckLine,
  Stack,
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from 'component-library';
import { useState } from 'react';
import { Trans } from 'react-i18next';

import {
  IndicatorProduct,
  IndicatorTiersEnum,
  IndicatorTiersType,
  VerifiedStatusEnum,
} from '@/api/rest/resources/types/IndicatorProduct';
import { CardLayout } from '@/components/widgets/a/Card/Card';
import { getStylesByCardType } from '@/components/widgets/a/Card/utils/getStylesByCardType';
import { PillWidget } from '@/components/widgets/a/Pill/Pill';
import { Body, Header } from '@/components/widgets/a/Text/Text';
import { VerifiedIcon } from '@/components/widgets/a/Verified/Verified';
import { isNullish } from '@/utils/booleans';

import { CalculationProgress } from './CalculationProgress';
import { IndicatorProductDetailsDialog } from './IndicatorProductDetailsDialog';

export const IndicatorProductCard = ({
  indicatorLibraryCard,
  className,
}: {
  indicatorLibraryCard: IndicatorProduct;
  className?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const { widget, count_of_applied_assets, tier, verification_information, id, show_calculation_progress } =
    indicatorLibraryCard;

  const { title, pill } = widget?.widget_format?.overview || {};
  const { data_sources = [] } = widget?.widget_format || {};

  const handleCloseIndicatorLibraryCardDetails = () => {
    setIsOpen(false);
  };

  return (
    <>
      <Stack
        data-testid='indicator-library-card'
        className={cn(
          'rounded-3xl border border-neutral-black-12 px-6 hover:bg-bg-light-grey hover:cursor-pointer',
          'w-[380px] min-h-[300px] h-full',
          className,
        )}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Tier tier={tier} />
        <CardLayout className='px-0 py-6'>
          <Title title={title} verification_information={verification_information} />
          {pill && <Body text={pill} />}
          <DataSources data_sources={data_sources} />
          <AppliedAssets
            count_of_applied_assets={count_of_applied_assets}
            id={id}
            show_calculation_progress={show_calculation_progress}
          />
        </CardLayout>
      </Stack>
      <IndicatorProductDetailsDialog
        open={isOpen}
        onClose={handleCloseIndicatorLibraryCardDetails}
        indicatorProduct={indicatorLibraryCard}
      />
    </>
  );
};

type IndicatorLibraryCardTitleProps = NonNullable<
  NonNullable<NonNullable<IndicatorProduct['widget']>['widget_format']>['overview']
> &
  Pick<IndicatorProduct, 'verification_information'>;

const Title = ({ title, verification_information }: IndicatorLibraryCardTitleProps) => {
  const isIndicatorVerified = verification_information?.status === VerifiedStatusEnum.verified;

  return (
    <Stack data-testid='indicator-library-card-title' direction='row' spacing={2} centerMain>
      {title && <Header text={title} />}
      {isIndicatorVerified && (
        <VerifiedIcon className='text-primary-100' verifiedBy={verification_information?.verified_by} />
      )}
    </Stack>
  );
};

const Tier = ({ tier }: Pick<IndicatorProduct, 'tier'>) => {
  if (!tier) {
    return null;
  }

  const isKnownTier = Object.values(IndicatorTiersEnum).includes(tier as IndicatorTiersEnum);
  const tierPill = isKnownTier
    ? {
        i18nPath: `global.widget.indicatorLibraryCard.tier.${tier}`,
        styles: getStylesByCardType(tier as IndicatorTiersType),
      }
    : {
        text: tier,
      };

  return <PillWidget {...tierPill} className='rounded-t-none py-2 px-3 mt-[-1px]' />; // mt-[-1px] in order to overlap the border of the card
};

const DataSources = ({ data_sources }: NonNullable<NonNullable<IndicatorProduct['widget']>['widget_format']>) => {
  if (!Array.isArray(data_sources) || data_sources.length === 0) {
    return null;
  }

  const text = data_sources.join(', ');

  return (
    <div data-testid='indicator-library-card-data-sources' className='flex flex-col'>
      <Header i18nPath='global.widget.indicatorLibraryCard.dataSources' className='uppercase' />
      <Tooltip>
        <TooltipTrigger className='text-start truncate'>
          <Body text={text} />
        </TooltipTrigger>
        <TooltipContent sideOffset={3} side='top' align='start'>
          {text}
          <TooltipArrow />
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

const AppliedAssets = ({
  count_of_applied_assets,
  id,
  show_calculation_progress,
}: Pick<IndicatorProduct, 'count_of_applied_assets' | 'id' | 'show_calculation_progress'>) => {
  if (isNullish(count_of_applied_assets)) {
    return null;
  }

  return (
    <>
      <Divider />
      <CalculationProgress id={id} show_calculation_progress={show_calculation_progress} />
      <Stack
        data-testid='indicator-library-card-applied-assets'
        className='typography-body2'
        direction='row'
        centerMain
        spacing={1}
      >
        <RiCheckLine className='text-primary-100' />
        <Trans
          i18nKey='global.widget.indicatorLibraryCard.appliedToAssets'
          values={{ assets: count_of_applied_assets }}
          components={{
            strong: <span className='typography-body1Semibold' />,
          }}
        />
      </Stack>
    </>
  );
};
